import React, { ForwardedRef } from 'react';
import { Node } from '@react-types/shared';
import { useListBox } from 'react-aria';
import { ListLayout, ListLayoutOptions } from '@react-stately/layout';
import { Virtualizer } from '@react-aria/virtualizer';
import { useListState } from 'react-stately';
import { getPlatformStyles, isMobile } from '@direct-frontend/platform-code';
import { genericForwardRef } from '@direct-frontend/stdlib';

import { TestProps } from '../../../internal/types/testTypes';
import { ListBoxOptionItem, ListBoxPropsRaw } from '../types';
import { ListBoxContext, useListBoxContent } from '../utils';
import { cnListBox } from '../../ListBoxBase/utils';
import { Option } from '../../ListBoxBase/Item/Item';
import { ListSize } from '../../ListBoxBase/types';
import { ListContext } from '../../List/utils';

getPlatformStyles({
    common: import('./VirtualList.css'),
    mobile: import('./VirtualList@mobile.css'),
});

export interface VirtualizerOptions<ValueType extends string> extends ListLayoutOptions<ListBoxOptionItem<ValueType>> {
    /**
     * Высота видимой области
     */
    height?: number;
}

export interface ListBoxVirtualizedPropsRaw<ValueType extends string, SectionType extends string = ValueType>
    extends ListBoxPropsRaw<
            ValueType,
            SectionType,
            /**
             * Не даём возможность управлять isTruncated.
             * isTruncated всегда true, чтобы верно высчитывалась высота строки.
             */
            Omit<ListBoxOptionItem<ValueType>, 'isTruncated'>
        >,
        TestProps {
    /**
     * Параметр виртуального списка
     *  Высота строки rowHeight выставляется автоматически в соответствии с переданным размером компонента
     *  @param height — высота видимой области
     */
    virtualizer?: Omit<VirtualizerOptions<ValueType>, 'rowHeight'>;
}

export type VirtualListProps<ValueType extends string> = ListBoxVirtualizedPropsRaw<ValueType>;

/**
 * Высота строки соответствует высоте TextInput: https://a.yandex-team.ru/arc_vcs/adv/frontend/packages/components/src/components/TextInput/TextInput.css?rev=4d49c38bd04f65c8cfeb03488487121f1982fbca#L137
 */
const rowHeightFromSize: {
    desktop: Record<ListSize, number>;
    mobile: Record<Extract<ListSize, 'm'>, number>;
} = {
    desktop: {
        m: 44,
        s: 36,
        xs: 32,
    },
    /**
     * Высота с учётом сепаратора: https://a.yandex-team.ru/arc_vcs/adv/frontend/packages/components/src/components/ListBoxBase/ListBoxBase@mobile.css?rev=4d49c38bd04f65c8cfeb03488487121f1982fbca#L45.
     * Размер только 'm' для мобильной версии.
     */
    mobile: {
        m: 61,
    },
};

const rowHeightFromSizeWithCaption: {
    desktop: Record<ListSize, number>;
    mobile: Record<Extract<ListSize, 'm'>, number>;
} = {
    desktop: {
        m: 60,
        s: 52,
        xs: 50,
    },
    mobile: {
        m: 77,
    },
};

export const VirtualList = genericForwardRef(function VirtualList<ValueType extends string>(
    props: ListBoxVirtualizedPropsRaw<ValueType>,
    forwardedRef: ForwardedRef<HTMLDivElement>,
) {
    const {
        virtualizer: externalVirtualizer,
        size,
        state: listBoxState,
        'data-testid': dataTestId = 'ListBoxVirtualized',
        logNode,
    } = props;

    const { ref, listProps, scrollClass } = useListBoxContent(props, forwardedRef);

    const hasCaption = React.useMemo(() => listProps.items.some(item => item.caption), [listProps]);

    const listPropsWithTruncated = React.useMemo(
        () => ({
            ...listProps,
            items: listProps.items.map(item => ({ ...item, isTruncated: true })),
        }),
        [listProps],
    );

    const defaultState = useListState(listPropsWithTruncated);

    const state = listBoxState ?? defaultState;

    const virtualizer: VirtualizerOptions<ValueType> | undefined = React.useMemo(() => {
        // При указании rowHeight в rtl-тестах рендерится только один элемент списка
        if (process.env.NODE_ENV === 'test' && !process.env.STORY_TO_SSR) {
            return externalVirtualizer;
        }

        const sizesRecordByCaption = hasCaption ? rowHeightFromSizeWithCaption : rowHeightFromSize;

        const rowHeight = isMobile() ? sizesRecordByCaption.mobile.m : sizesRecordByCaption.desktop[size];
        const visibleItemsCount = Math.min(state?.collection.size ?? 0, 10);

        return {
            /**
             * явное указание высоты нужно для того, чтобы Vrtualier понимал свою высоту
             * иначе рендерится вся коллекция и при размере более 10000 элементов страница падает
             */
            height: visibleItemsCount * rowHeight,
            ...externalVirtualizer,
            rowHeight,
        };
    }, [hasCaption, size, state?.collection.size, externalVirtualizer]);

    const layout = React.useMemo(() => new ListLayout<ListBoxOptionItem<ValueType>>(virtualizer), [virtualizer]);
    layout.collection = state.collection;
    layout.disabledKeys = state.disabledKeys;
    const { listBoxProps } = useListBox(
        {
            ...listPropsWithTruncated,
            isVirtualized: true,
            keyboardDelegate: layout,
        },
        state,
        ref,
    );

    return (
        //Если пробрасывать state в ListBoxOption через props, то не работает управление с клавиатуры
        //Поэтому пришлось обернуть в ListBoxContext
        <ListBoxContext.Provider value={state}>
            <ListContext.Provider value={{ size: size }}>
                <Virtualizer
                    {...listBoxProps}
                    className={cnListBox('list', { 'row-height': Boolean(virtualizer?.rowHeight) }, [scrollClass])}
                    style={{ height: virtualizer?.height }}
                    sizeToFit="height"
                    scrollDirection="vertical"
                    layout={layout}
                    focusedKey={state.selectionManager.focusedKey}
                    collection={state.collection}
                    ref={ref}
                    data-testid={dataTestId}
                >
                    {(type: string, node: Node<ListBoxOptionItem<ValueType>>) => {
                        const item = node?.value;

                        if (!item) {
                            return null;
                        }

                        return (
                            <Option
                                item={item}
                                state={state}
                                data-testid={`${dataTestId}.${item.value}`}
                                logNode={
                                    logNode
                                        ? `${typeof logNode === 'string' ? logNode : (logNode.name ?? '')}.${item.value}`
                                        : undefined
                                }
                            >
                                {node.rendered}
                            </Option>
                        );
                    }}
                </Virtualizer>
            </ListContext.Provider>
        </ListBoxContext.Provider>
    );
});
